<template>
  <v-dialog v-model="dialog" persistent max-width="1100" overlay-color="grey darken-3">
    <v-card class="rounded-xl overflow-hidden shadow-24">
      <!-- HEADER PRINCIPAL -->
      <v-toolbar flat color="primary" dark class="cabecera-armario">
        <v-icon left size="28" class="hidden-xs-only">mdi-folder-information</v-icon>
        <v-toolbar-title class="titulo-armario font-weight-bold" :title="`${region.name} · UID ${region.id}`">
          <span class="titulo-prefijo">Gestión de</span> <span class="titulo-nombre">{{ region.name }}</span>
        </v-toolbar-title>
        <v-spacer></v-spacer>
        
        <template v-if="!bloqueado">
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <v-btn icon class="hidden-xs-only" aria-label="Ver en el árbol" v-bind="attrs" v-on="on" @click="$emit('ver-en-arbol', region)">
                <v-icon>mdi-file-tree-outline</v-icon>
              </v-btn>
            </template>
            <span>Ver en el árbol</span>
          </v-tooltip>
          <v-tooltip v-if="verPlanos" bottom>
            <template #activator="{ on, attrs }">
              <v-btn icon class="hidden-xs-only" aria-label="Ver en el plano" v-bind="attrs" v-on="on" @click="$emit('ver-en-plano', region)">
                <v-icon>mdi-map-marker-outline</v-icon>
              </v-btn>
            </template>
            <span>Ver en el plano</span>
          </v-tooltip>
          <v-btn icon class="ml-1" aria-label="Cerrar" @click="close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </template>
      </v-toolbar>

      <!-- Nota interna del armario (solo llega a los usuarios de Ramos) -->
      <div v-if="region && region.nota" class="nota-armario d-flex align-start">
        <v-icon small color="amber darken-3" class="mr-2 mt-1">mdi-note-text</v-icon>
        <div class="texto-nota">{{ region.nota }}</div>
      </div>

      <!-- NAVEGACIÓN DE TABS (ARTÍCULOS SIEMPRE VISIBLE) -->
      <v-tabs v-model="activeTab" color="primary" height="44" slider-size="3" class="pestanas-armario" :grow="$vuetify.breakpoint.xsOnly" :show-arrows="false">
        <v-tab class="font-weight-bold text-none">
          <v-icon left class="hidden-xs-only">mdi-paperclip</v-icon> Adjuntos ({{ adjuntos.length }})
        </v-tab>
        <v-tab class="font-weight-bold text-none">
          <v-icon left class="hidden-xs-only">mdi-package-variant-closed</v-icon>
          Artículos
          <v-chip v-if="!puedeVerArticulos" x-small color="amber darken-2" dark class="ml-2 font-weight-black" label>
            PRO
          </v-chip>
        </v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeTab">
        <!-- TAB ADJUNTOS (Igual que antes) -->
        <v-tab-item class="pa-4 grey lighten-5">
          <!-- una sección por carpeta del armario; los archivos se sueltan encima de la carpeta -->
          <div @dragover.prevent @drop.prevent="onSoltarFuera">
            <div v-if="subida" class="barra-subida">
              <v-progress-linear :value="(subida.hechos / subida.total) * 100" color="primary" height="4" />
              <div class="text-caption pa-2">Subiendo {{ Math.min(subida.hechos + 1, subida.total) }} de {{ subida.total }}: {{ subida.nombre }}</div>
            </div>
            <div class="vista-carpetas">
              <div class="barra-documentos d-flex align-center">
                <v-text-field v-model="searchAdjuntos" prepend-inner-icon="mdi-magnify" placeholder="Filtrar documentos" solo flat dense hide-details clearable class="filtro-documentos rounded-lg" />
                <!-- las carpetas se eligen del catálogo; las que tienen documentos no se pueden quitar -->
                <v-menu v-if="puedeGestionarCarpetas" offset-y left :close-on-content-click="false">
                  <template #activator="{ on, attrs }">
                    <v-btn text color="primary" class="text-none rounded-lg" :loading="guardandoCarpetas" v-bind="attrs" v-on="on">
                      <v-icon left>mdi-folder-multiple-outline</v-icon> Carpetas
                    </v-btn>
                  </template>
                  <v-list dense class="menu-carpetas">
                    <v-subheader>Carpetas de este armario</v-subheader>
                    <v-list-item v-for="c in opcionesCarpetas" :key="c.id" :disabled="c.bloqueada" @click="alternarCarpeta(c.id)">
                      <v-list-item-action class="my-0 mr-3">
                        <v-simple-checkbox :value="c.marcada" :disabled="c.bloqueada" color="primary" @input="alternarCarpeta(c.id)" />
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title>{{ c.nombre }}</v-list-item-title>
                        <v-list-item-subtitle>máx. {{ c.limite }}<template v-if="c.bloqueada"> · tiene documentos</template></v-list-item-subtitle>
                      </v-list-item-content>
                      <v-icon v-if="c.bloqueada" small color="grey">mdi-lock-outline</v-icon>
                    </v-list-item>
                  </v-list>
                </v-menu>
                <v-tooltip v-if="puedeAdjuntar" bottom :disabled="hayHueco">
                  <template #activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on">
                      <v-btn color="primary" depressed class="rounded-lg text-none" :disabled="!hayHueco" @click="subirA(null)">
                        <v-icon left>mdi-cloud-upload-outline</v-icon> Subir
                      </v-btn>
                    </span>
                  </template>
                  <span>{{ carpetasArmario.length ? "Todas las carpetas están llenas: elimina un documento para subir otro" : "El armario no tiene carpetas: elige primero en cuáles se guardarán sus documentos" }}</span>
                </v-tooltip>
              </div>

              <div v-if="!grupos.length" class="pa-6 text-center grey--text text--darken-1">
                <v-icon size="40" color="grey lighten-1">mdi-folder-outline</v-icon>
                <div class="mt-2">Este armario todavía no tiene carpetas.</div>
                <div v-if="puedeGestionarCarpetas" class="text-caption mt-1">Elige en «Carpetas» las que necesita para poder subir sus documentos.</div>
              </div>

              <div
                v-for="g in grupos"
                :key="g.clave"
                class="grupo-carpeta"
                :class="{ 'grupo-destino': destinoArrastre === g.clave, 'grupo-lleno': g.lleno, 'grupo-sin-carpeta': g.id === null }"
                @dragenter.prevent.stop="onArrastrarSobreGrupo(g, $event)"
                @dragover.prevent.stop="onArrastrarSobreGrupo(g, $event)"
                @dragleave="onSalirDeGrupo(g, $event)"
                @drop.prevent.stop="onSoltarEnGrupo(g, $event)"
              >
                <div class="cabecera-carpeta d-flex align-center">
                  <v-icon class="mr-2" :color="g.id === null ? 'grey' : g.documentos.length ? 'amber darken-2' : 'amber lighten-3'">
                    {{ g.id === null ? "mdi-folder-question-outline" : g.documentos.length ? "mdi-folder" : "mdi-folder-outline" }}
                  </v-icon>
                  <span class="font-weight-bold nombre-carpeta">{{ g.nombre }}</span>
                  <v-chip v-if="g.limite" x-small class="ml-2 font-weight-bold" :color="g.lleno ? 'orange lighten-4' : 'grey lighten-3'" :text-color="g.lleno ? 'orange darken-4' : 'grey darken-2'">
                    {{ g.documentos.length }}/{{ g.limite }}
                  </v-chip>
                  <v-spacer />
                  <v-tooltip v-if="puedeAdjuntar && g.id !== null" top>
                    <template #activator="{ on, attrs }">
                      <span v-bind="attrs" v-on="on">
                        <v-btn icon small color="primary" :disabled="g.lleno" :aria-label="`Subir a ${g.nombre}`" @click="subirA(g.id)">
                          <v-icon small>mdi-upload</v-icon>
                        </v-btn>
                      </span>
                    </template>
                    <span>{{ g.lleno ? `Llena (${g.documentos.length}/${g.limite}): elimina un documento para subir otro` : `Subir a ${g.nombre}` }}</span>
                  </v-tooltip>
                </div>

                <div v-if="g.id === null && puedeMoverDocumentos" class="text-caption grey--text text--darken-1 pb-1 pl-8">
                  Documentos de antes de las carpetas: muévelos a la carpeta que les corresponda.
                </div>
                <div v-if="!g.visibles.length" class="text-caption grey--text py-2 pl-8">
                  <template v-if="g.documentos.length">Ningún documento coincide con el filtro.</template>
                  <template v-else>Vacía<span v-if="puedeAdjuntar && !pantallaTactil"> · arrastra aquí los archivos para subirlos</span></template>
                </div>
                <!-- en ordenador los documentos se mueven arrastrándolos a otra carpeta -->
                <div
                  v-for="item in g.visibles"
                  :key="item.id"
                  class="fila-documento d-flex align-center"
                  :class="{ 'acciones-al-pasar': !pantallaTactil, 'fila-arrastrable': puedeArrastrarDocumentos, 'fila-arrastrada': documentoArrastrado && documentoArrastrado.id === item.id }"
                  :draggable="puedeArrastrarDocumentos"
                  @dragstart="onArrastrarDocumento(item, $event)"
                  @dragend="documentoArrastrado = null; destinoArrastre = null"
                >
                  <div class="flex-grow-1 min-w-0 texto-documento">
                    <a href="#" class="nombre-documento text-wrap-cell" :title="puedeArrastrarDocumentos ? 'Ver · arrastra para moverlo a otra carpeta' : 'Ver'" @click.prevent="visualizarAdjunto(item)">{{ item.name }}</a>
                    <div class="text-caption grey--text text--darken-1">
                      <span v-if="item.descripcion">{{ item.descripcion }} · </span>
                      {{ item.usuario?.username || "—" }}<span v-if="item.created_at"> · {{ formatFecha(item.created_at) }}</span>
                    </div>
                  </div>
                  <div class="text-no-wrap acciones-documento">
                    <v-btn icon small color="grey darken-1" title="Previsualizar" :aria-label="`Previsualizar ${item.name}`" @click="visualizarAdjunto(item)"><v-icon small>mdi-eye-outline</v-icon></v-btn>
                    <v-btn icon small color="grey darken-1" title="Descargar" :aria-label="`Descargar ${item.name}`" :loading="descargando === item.id" @click="descargarAdjunto(item)"><v-icon small>mdi-download</v-icon></v-btn>
                    <!-- en pantallas táctiles no se puede arrastrar: se mueve con el menú -->
                    <v-menu v-if="puedeMoverDocumentos && pantallaTactil" offset-y left>
                      <template #activator="{ on, attrs }">
                        <v-btn icon small color="grey darken-1" title="Mover a otra carpeta" :aria-label="`Mover ${item.name}`" v-bind="attrs" v-on="on"><v-icon small>mdi-folder-move-outline</v-icon></v-btn>
                      </template>
                      <v-list dense>
                        <v-subheader>Mover a…</v-subheader>
                        <v-list-item
                          v-for="d in destinosPara(item)"
                          :key="d.clave"
                          :disabled="d.lleno"
                          @click="moverDocumento(item, d.id)"
                        >
                          <v-icon small class="mr-2" color="amber darken-2">mdi-folder</v-icon>
                          <v-list-item-title>{{ d.nombre }}<span class="grey--text"> ({{ d.documentos.length }}/{{ d.limite }})</span></v-list-item-title>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                    <v-btn v-if="puedeEliminar" icon small color="error lighten-1" title="Eliminar" :aria-label="`Eliminar ${item.name}`" @click="eliminarAdjunto(item.id)"><v-icon small>mdi-delete-outline</v-icon></v-btn>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </v-tab-item>

        <!-- TAB ARTÍCULOS (LÓGICA DE MARKETING) -->
        <v-tab-item class="pa-4 grey lighten-5">
          <!-- CASO 1: TIENE PERMISO -->
          <v-card v-if="puedeVerArticulos" flat outlined class="rounded-lg">
            <v-data-table :headers="headersArticulos" :items="articulos" :items-per-page="5" :search="searchArticulos" class="rounded-lg">
              <template #top>
                <v-text-field v-model="searchArticulos" label="Buscar en inventario..." class="pa-4" prepend-inner-icon="mdi-magnify" outlined dense hide-details />
              </template>
            </v-data-table>
          </v-card>

          <!-- CASO 2: ESTRATEGIA DE MARKETING (LOCK) -->
          <v-card v-else flat class="pa-10 text-center rounded-lg border-gold bg-white">
            <v-row justify="center" align="center">
              <v-col cols="12" md="8">
                <div class="premium-icon-container mb-4">
                  <v-icon size="100" color="amber darken-3">mdi-shield-star</v-icon>
                </div>
                <h2 class="text-h4 font-weight-black grey--text text--darken-3 mb-2">
                  Gestión de Inventario PRO
                </h2>
                <p class="text-subtitle-1 grey--text text--darken-1 mb-6">
                  Vincula piezas de repuesto, herramientas y consumibles directamente a tus activos. 
                  Optimiza tus tiempos de mantenimiento sabiendo exactamente qué necesitas antes de empezar.
                </p>
                
                <!-- <v-row class="mb-6 text-left" dense>
                  <v-col cols="12" sm="6" v-for="(feature, i) in ['Stock en tiempo real', 'Alertas de repuestos', 'Historial de consumos', 'Códigos QR por pieza']" :key="i">
                    <div class="d-flex align-center">
                      <v-icon color="success" class="mr-2">mdi-check-circle-outline</v-icon>
                      <span class="font-weight-medium grey--text text--darken-2">{{ feature }}</span>
                    </div>
                  </v-col>
                </v-row>

                <v-btn color="amber darken-3" dark x-large class="rounded-xl px-10 font-weight-black elevation-4">
                  <v-icon left>mdi-rocket-launch</v-icon>
                  ¡LO QUIERO! - DESBLOQUEAR AHORA
                </v-btn>
                
                <div class="mt-4">
                  <a href="#" class="caption primary--text font-weight-bold text-decoration-none">Ver todas las ventajas de la versión Premium</a>
                </div> -->
              </v-col>
            </v-row>
          </v-card>
        </v-tab-item>
      </v-tabs-items>

    </v-card>

    <VisorArchivo ref="visor" />
  </v-dialog>
</template>

<script>
import { mapState } from 'vuex';
import axios from 'axios';
import VisorArchivo from "./VisorArchivo.vue";
import { guardarArchivo } from "@/utils/archivos";


export default {
  components: { VisorArchivo },
  props: {
    dialog: { type: Boolean, required: true },
    adjuntos: { type: Array, default: () => [] },
    articulos: { type: Array, default: () => [] },
    region: { type: Object, default: null },

    verPlanos: {type: Boolean, default: true},
    puedeEditar: {type: Boolean, default: true},

    puedeAdjuntar: {type: Boolean, default: true},
    puedeEliminar: {type: Boolean, default: true},

    // acceso por QR: no se puede cerrar ni salir a otras vistas
    bloqueado: {type: Boolean, default: false},
  },

  data() {
    return {
      API_URL: process.env.VUE_APP_LOCAL_BASE_URL,
      searchAdjuntos: '',
      searchArticulos: '',
      headersArticulos: [
        { text: 'Referencia', value: 'referencia', sortable: true },
        { test: 'C. de barras', value: 'barcode', sortable: true },
        { test: 'Nombre', value: 'nombre', sortable: true },
        { test: 'Cant.', value: 'cantidad' }
      ],
      descargando: null,
      // carpetas de documentos asignadas al armario (con su límite)
      carpetasArmario: [],
      guardandoCarpetas: false,
      destinoArrastre: null,
      documentoArrastrado: null,
      pantallaTactil: typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(hover: none)').matches,
      subida: null, // { total, hechos, nombre }

      activeTab: 0,
      searchAdjuntos: "",
      searchArticulos: "",
    };
  },

  computed: {
    ...mapState(['user', 'usuarios', 'permisos', 'esRamos']),
    ...mapState("armarios", ["stateSelectedNode", "carpetas"]),

    idCliente() {
      return this.$store.state.idCliente || localStorage.getItem('clienteSeleccionado');
    },
    idArmario() {
      return this.region ? this.region.id_armario || this.region.id : null;
    },
    // las carpetas las organiza Ramos (y no en el acceso por QR)
    puedeGestionarCarpetas() {
      return this.puedeEditar && this.esRamos && !this.bloqueado;
    },
    puedeMoverDocumentos() {
      return this.puedeAdjuntar && this.esRamos && !this.bloqueado;
    },
    puedeArrastrarDocumentos() {
      return this.puedeMoverDocumentos && !this.pantallaTactil && !this.subida;
    },
    // catálogo para el desplegable: las carpetas con documentos no se pueden quitar
    opcionesCarpetas() {
      return this.carpetas.map((c) => {
        const documentos = this.adjuntos.filter((a) => a.id_carpeta === c.id).length;
        const marcada = this.carpetasArmario.some((a) => a.id === c.id);
        return { id: c.id, nombre: c.nombre, limite: c.limite, marcada, bloqueada: marcada && documentos > 0 };
      });
    },
    hayHueco() {
      return this.grupos.some((g) => g.id !== null && !g.lleno);
    },
    // una sección por carpeta asignada y, si queda algún documento de antes de las carpetas, "Sin carpeta"
    grupos() {
      const q = (this.searchAdjuntos || '').trim().toLowerCase();
      const coincide = (a) => !q || [a.name, a.descripcion, a.usuario?.username].some((t) => (t || '').toLowerCase().includes(q));
      const asignadas = new Set(this.carpetasArmario.map((c) => c.id));
      const grupos = this.carpetasArmario.map((c) => {
        const documentos = this.adjuntos.filter((a) => a.id_carpeta === c.id);
        return {
          clave: `c${c.id}`, id: c.id, nombre: c.nombre, limite: c.limite,
          documentos, visibles: documentos.filter(coincide), lleno: documentos.length >= c.limite,
        };
      });
      const sueltos = this.adjuntos.filter((a) => a.id_carpeta == null || !asignadas.has(a.id_carpeta));
      if (sueltos.length) {
        grupos.push({ clave: 'sin', id: null, nombre: 'Sin carpeta', limite: null, documentos: sueltos, visibles: sueltos.filter(coincide), lleno: false });
      }
      return grupos;
    },
    puedeVerArticulos(){
      return this.permisos.includes('ver-piezas-armarios');
    }
  },

  watch: {
    // al abrir otro armario (o reabrir la ventana) se cargan sus carpetas
    idArmario: { immediate: true, handler() { this.cargarCarpetasArmario(); } },
    dialog(abierto) { if (abierto) this.cargarCarpetasArmario(); },
  },

  methods: {
    async cargarCarpetasArmario() {
      if (!this.idArmario || !this.idCliente) { this.carpetasArmario = []; return; }
      if (!this.carpetas.length) this.$store.dispatch('armarios/cargarCarpetas');
      const id = this.idArmario;
      try {
        const { data } = await axios.get(`${process.env.VUE_APP_LOCAL_BASE_URL}/api/nodejs/clientes/${this.idCliente}/armarios/${id}/carpetas`);
        if (id === this.idArmario) this.carpetasArmario = data.carpetas || [];
      } catch (error) {
        console.error('Error al cargar las carpetas del armario:', error);
        this.carpetasArmario = [];
      }
    },
    async guardarCarpetasArmario(ids) {
      this.guardandoCarpetas = true;
      try {
        const { data } = await axios.put(`${process.env.VUE_APP_LOCAL_BASE_URL}/api/nodejs/clientes/${this.idCliente}/armarios/${this.idArmario}/carpetas`, { ids });
        this.carpetasArmario = data.carpetas || [];
      } catch (error) {
        this.$avisar(error.response?.data?.error || 'No se pudieron guardar las carpetas', 'error');
      } finally {
        this.guardandoCarpetas = false;
      }
    },
    alternarCarpeta(id) {
      const opcion = this.opcionesCarpetas.find((c) => c.id === id);
      if (!opcion || opcion.bloqueada || this.guardandoCarpetas) return;
      const ids = this.carpetasArmario.map((c) => c.id);
      this.guardarCarpetasArmario(opcion.marcada ? ids.filter((x) => x !== id) : [...ids, id]);
    },
    /** Abre la subida (diálogo del padre) con la carpeta de destino ya elegida */
    subirA(id_carpeta) {
      this.$emit('add-adjunto', { region: this.region, id_carpeta, carpetas: this.carpetasArmario.map((c) => ({ ...c, documentos: this.adjuntos.filter((a) => a.id_carpeta === c.id).length })) });
    },
    destinosPara(item) {
      // "Sin carpeta" no es un destino: los documentos van siempre en una carpeta
      return this.grupos.filter((g) => g.id !== null && g.id !== item.id_carpeta);
    },
    async moverDocumento(item, id_carpeta) {
      try {
        await axios.put(`${process.env.VUE_APP_LOCAL_BASE_URL}/api/nodejs/clientes/${this.idCliente}/adjuntos-armario/${item.id}/carpeta`, {
          id_armario: this.idArmario, id_carpeta, nombre: item.name,
        });
        this.$emit('refresh-adjuntos');
      } catch (error) {
        this.$avisar(error.response?.data?.error || 'No se pudo mover el documento', 'error');
      }
    },

    formatFecha(fechaISO) {
      if (!fechaISO) return '—';
      
      try {
        const fecha = new Date(fechaISO);
        return new Intl.DateTimeFormat('es-ES', {
          timeZone: 'Europe/Madrid',
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        }).format(fecha);
      } catch (e) {
        return fechaISO;
      }
    },
    // ---------------- descarga y vista previa de adjuntos ----------------
    /** URL de descarga con el cliente (con portal externo, el archivo se pide a su portal) */
    urlAdjunto(item) {
      const cliente = localStorage.getItem('clienteSeleccionado');
      const consulta = cliente ? `?id_cliente=${encodeURIComponent(cliente)}` : '';
      return `${process.env.VUE_APP_LOCAL_BASE_URL}/api/nodejs/armarios/adjuntos/${item.id}/download${consulta}`;
    },

    /** Contenido del adjunto como Blob (con la sesión; algunos adjuntos externos vienen ya en base64) */
    async obtenerBlob(item) {
      if (item.contenido) {
        const bytes = Uint8Array.from(atob(item.contenido), (c) => c.charCodeAt(0));
        return new Blob([bytes], { type: item.tipo && item.tipo.includes('/') ? item.tipo : 'application/pdf' });
      }
      const { data } = await axios.get(this.urlAdjunto(item), { responseType: 'blob' });
      return data;
    },


    /** Botón de descarga: guarda el archivo en el equipo */
    async descargarAdjunto(item) {
      this.descargando = item.id;
      try {
        guardarArchivo(item, await this.obtenerBlob(item));
      } catch (error) {
        console.error('Error al descargar el adjunto:', error);
        this.$emit('error', 'No se pudo descargar el adjunto.');
      } finally {
        this.descargando = null;
      }
    },

    /** Botón del ojo: abre el visor (PDF página a página, imágenes; el resto, opción de descargar) */
    visualizarAdjunto(item) {
      this.$refs.visor.abrir(item, () => this.obtenerBlob(item));
    },

    // ---------------- subir arrastrando archivos ----------------
    hayArchivos(e) {
      return this.puedeAdjuntar && [...(e.dataTransfer?.types || [])].includes('Files');
    },
    onArrastrarDocumento(item, e) {
      this.documentoArrastrado = item;
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', item.name);
    },
    onArrastrarSobreGrupo(g, e) {
      // un documento solo se marca destino en otra carpeta con hueco
      const destinoDocumento = this.documentoArrastrado && g.id !== null && g.id !== this.documentoArrastrado.id_carpeta && !g.lleno;
      if ((this.hayArchivos(e) && !this.subida && g.id !== null) || destinoDocumento) this.destinoArrastre = g.clave;
    },
    onSalirDeGrupo(g, e) {
      if (!e.currentTarget.contains(e.relatedTarget) && this.destinoArrastre === g.clave) this.destinoArrastre = null;
    },
    async onSoltarEnGrupo(g, e) {
      this.destinoArrastre = null;
      if (this.documentoArrastrado) return this.soltarDocumento(this.documentoArrastrado, g);
      if (!this.hayArchivos(e) || this.subida) return;
      if (g.id === null) return this.$avisar('Suelta los archivos sobre una carpeta', 'warning');
      const archivos = [...e.dataTransfer.files];
      const hueco = g.limite - g.documentos.length;
      if (hueco <= 0) return this.$avisar(`"${g.nombre}" está llena (${g.documentos.length}/${g.limite}): elimina un documento para subir otro`, 'warning');
      if (archivos.length > hueco) {
        this.$avisar(`En "${g.nombre}" solo caben ${hueco} más: se suben los ${hueco} primeros`, 'warning');
      }
      await this.subirArchivos(archivos.slice(0, hueco), g.id);
    },
    // fuera de una carpeta no se sube nada (y el navegador no abre el archivo)
    async soltarDocumento(item, g) {
      this.documentoArrastrado = null;
      if (g.id === item.id_carpeta) return;
      if (g.id === null) return this.$avisar('Los documentos van siempre en una carpeta', 'warning');
      if (g.lleno) return this.$avisar(`"${g.nombre}" está llena (${g.documentos.length}/${g.limite}): elimina un documento para mover otro`, 'warning');
      await this.moverDocumento(item, g.id);
    },
    onSoltarFuera(e) {
      if (this.documentoArrastrado) return;
      if (this.hayArchivos(e) && !this.subida) this.$avisar('Suelta los archivos sobre una carpeta', 'warning');
    },
    async subirArchivos(archivos, id_carpeta) {
      if (!archivos.length) return;

      const idArmario = this.region.id_armario || this.region.id;
      const idUsuario = this.user?.id || JSON.parse(localStorage.getItem('user') || 'null')?.id;
      let fallos = 0;
      this.subida = { total: archivos.length, hechos: 0, nombre: '' };
      for (const archivo of archivos) {
        // el nombre del documento es el del archivo sin la extensión (se puede cambiar después)
        const nombre = archivo.name.replace(/\.[^.]+$/, '') || archivo.name;
        this.subida.nombre = nombre;
        const datos = new FormData();
        datos.append('archivo', archivo);
        datos.append('name', nombre);
        datos.append('description', '');
        datos.append('id_usuario', idUsuario);
        datos.append('tipo', 'armario');
        datos.append('id_cliente', localStorage.getItem('clienteSeleccionado'));
        datos.append('id_carpeta', id_carpeta);
        try {
          await axios.post(`${process.env.VUE_APP_LOCAL_BASE_URL}/api/nodejs/armarios/${idArmario}/adjuntos`, datos, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        } catch (error) {
          console.error(`Error al subir ${archivo.name}:`, error);
          if (error.response?.status === 409) this.$avisar(error.response.data.error, 'warning');
          fallos++;
        }
        this.subida.hechos++;
      }
      this.subida = null;
      const hechos = archivos.length - fallos;
      if (fallos) this.$avisar(`Subidos ${hechos} de ${archivos.length} archivos. ${fallos} no se pudieron subir.`, 'warning');
      else this.$avisar(`${hechos} archivo${hechos > 1 ? 's' : ''} subido${hechos > 1 ? 's' : ''}`, 'success');
      this.$emit('refresh-adjuntos');
    },

    async eliminarAdjunto(idAdjunto) {
      const adjunto = this.adjuntos.find((a) => a.id === idAdjunto);
      const ok = await this.$confirmar({
        titulo: "Eliminar adjunto",
        texto: `Se eliminará ${adjunto ? `"${adjunto.name}"` : "este adjunto"} del armario.`,
        peligro: true,
      });
      if (!ok) return;
      try {
        await axios.delete(`${process.env.VUE_APP_LOCAL_BASE_URL}/api/nodejs/armarios/adjuntos/${idAdjunto}`,
          {
            data: {
              id_cliente: localStorage.getItem('clienteSeleccionado'),
              // para el historial (con portal externo el backend no sabe de qué armario es)
              id_armario: this.region.id_armario || this.region.id,
              nombre: adjunto ? adjunto.name : undefined,
            }
          }
        );
        this.$emit('refresh-adjuntos'); 
      } catch (error) {
        console.error("Error al eliminar el adjunto:", error);
        this.$emit('error', "No se pudo eliminar el adjunto");
      }
    },

    formatFecha(fechaISO) {
      if (!fechaISO) return '';
      const fecha = new Date(fechaISO);
      return `${fecha.getDate().toString().padStart(2,'0')}/${
        (fecha.getMonth()+1).toString().padStart(2,'0')}/${
        fecha.getFullYear()} ${fecha.getHours().toString().padStart(2,'0')}:${
        fecha.getMinutes().toString().padStart(2,'0')}`;
    },

    close() {
      this.$refs.visor?.cerrar();
      this.$emit("close")
    }
  },
};
</script>

<style scoped>
/* Título: en escritorio en una línea; en el móvil el nombre completo en varias líneas */
.titulo-armario {
  font-size: 1.5rem;
}
@media (max-width: 599px) {
  .cabecera-armario,
  .cabecera-armario >>> .v-toolbar__content {
    height: auto !important;
    min-height: 56px;
  }
  .cabecera-armario >>> .v-toolbar__content {
    padding-top: 8px;
    padding-bottom: 8px;
  }
  .titulo-armario {
    flex: 1 1 auto;
    min-width: 0;
    margin-right: 8px;
    white-space: normal;
    overflow: visible;
    line-height: 1.25;
  }
  .titulo-prefijo {
    display: block;
    font-size: 0.75rem;
    font-weight: 500;
    opacity: 0.8;
  }
  .titulo-nombre {
    display: block;
    font-size: 1.1rem;
    overflow-wrap: anywhere;
  }
}

.nota-armario {
  padding: 10px 16px;
  background: #fff8e1;
  border-bottom: 1px solid #ffe082;
  color: #5d4037;
  font-size: 0.875rem;
}
.texto-nota {
  white-space: pre-line;
  overflow-wrap: anywhere;
}
.pestanas-armario {
  border-bottom: 1px solid #eceff1;
}
/* en el móvil sin el hueco de las flechas: las dos pestañas caben enteras */
@media (max-width: 599px) {
  .pestanas-armario >>> .v-slide-group__prev,
  .pestanas-armario >>> .v-slide-group__next {
    display: none !important;
  }
  .pestanas-armario >>> .v-tab {
    padding: 0 8px;
    min-width: 0;
  }
}
/* Barra de los documentos: filtro, carpetas y subir */
.barra-documentos {
  gap: 8px;
  margin-bottom: 12px;
}
.filtro-documentos {
  flex: 1 1 auto;
  border: 1px solid #e3e8ec;
}
/* Carpetas de documentos */
.grupo-carpeta {
  margin: 0 0 10px;
  border: 1px solid #e8ecef;
  border-radius: 10px;
  background: white;
  transition: border-color 0.15s ease, background-color 0.15s ease;
}
.grupo-destino {
  border: 2px dashed #117f9c;
  background: #e6f3f6;
}
.grupo-sin-carpeta {
  background: #fafbfc;
  border-style: dashed;
}
.cabecera-carpeta {
  padding: 4px 8px 4px 12px;
  min-height: 40px;
  border-bottom: 1px solid #f2f4f5;
}
.nombre-carpeta {
  color: #37474f;
}
.fila-documento {
  padding: 6px 8px;
  border-bottom: 1px solid #f5f6f7;
}
.nombre-documento {
  font-weight: 600;
  color: #263238;
  text-decoration: none;
}
.nombre-documento:hover {
  color: #117f9c;
  text-decoration: underline;
}
/* en ordenador las acciones de cada documento solo se ven al pasar por encima */
.acciones-al-pasar .acciones-documento {
  opacity: 0;
  transition: opacity 0.12s ease;
}
.acciones-al-pasar:hover .acciones-documento,
.acciones-al-pasar:focus-within .acciones-documento {
  opacity: 1;
}
.fila-arrastrable {
  cursor: grab;
}
.fila-arrastrable:hover {
  background: #f7fafb;
}
.fila-arrastrada {
  opacity: 0.4;
}
.fila-documento:last-child {
  border-bottom: none;
}
.texto-documento {
  padding-left: 32px;
}
/* en el móvil las acciones van debajo del texto para que se lea entero */
@media (max-width: 599px) {
  .fila-documento {
    flex-wrap: wrap;
  }
  .texto-documento {
    padding-left: 8px;
    flex-basis: 100%;
  }
  .acciones-documento {
    margin-left: auto;
  }
}
.min-w-0 {
  min-width: 0;
}
.barra-subida {
  border-bottom: 1px solid #e0e4e9;
  color: #455a64;
}
.border-gold {
  border: 2px dashed #ffb300 !important;
  background: linear-gradient(to bottom right, #ffffff, #fffdf2);
}

.premium-icon-container {
  animation: float 3s ease-in-out infinite;
}

@keyframes float {
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
}

.shadow-24 {
  box-shadow: 0 24px 38px 3px rgba(0,0,0,0.14), 0 9px 46px 8px rgba(0,0,0,0.12), 0 11px 15px -7px rgba(0,0,0,0.2) !important;
}

/* Mejora el visor de archivos para que parezca profesional */
.pdf-container { width: 100%; height: 100%; background: #333; }

.text-wrap-cell {
  white-space: normal !important; /* Permite saltos de línea */
  word-break: break-word;        /* Rompe palabras si son demasiado largas */
  line-height: 1.4;              /* Mejora la legibilidad */
  padding: 8px 0;                /* Espaciado para que no se peguen las líneas */
  min-width: 120px;              /* Ancho mínimo para evitar columnas colapsadas */
}

/* Opcional: ajustar el ancho de la columna de fecha para que no baile */
.text-no-wrap {
  white-space: nowrap !important;
}
</style>